<script setup>
import { computed, ref } from 'vue';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';

// Rendered only when the parent has confirmed the inbox has Captain
// available (see `captain_available` on the conversation payload).
const props = defineProps({
  conversationId: { type: [Number, String], required: true },
  captainMode: { type: String, required: true },
});

const store = useStore();
const { t } = useI18n();

const isLoading = ref(false);

// Persistent, explicit Captain on/off - Conversation#captain_mode, deliberately
// independent of conversation status. A human reply no longer flips this (see
// Enterprise::Message); only an explicit Take Over / Activate Captain does.
const isCaptainActive = computed(() => props.captainMode === 'always_active');

const setCaptainMode = captainMode => {
  isLoading.value = true;
  store
    .dispatch('toggleCaptainMode', {
      conversationId: props.conversationId,
      captainMode,
    })
    .then(() => useAlert(t('CONVERSATION.CHANGE_STATUS')))
    .finally(() => {
      isLoading.value = false;
    });
};

const takeOver = () => setCaptainMode('manual_off');
const activateCaptain = () => setCaptainMode('always_active');
</script>

<template>
  <div
    class="flex items-center flex-shrink-0 gap-1.5 px-2 py-1 rounded-lg outline-1 outline outline-n-container"
  >
    <span
      class="flex-shrink-0 rounded-full size-2"
      :class="isCaptainActive ? 'bg-n-teal-9' : 'bg-n-slate-9'"
    />
    <span class="text-xs font-medium whitespace-nowrap text-n-slate-11">
      {{
        isCaptainActive
          ? t('CONVERSATION.CAPTAIN_TAKEOVER.CAPTAIN_ACTIVE')
          : t('CONVERSATION.CAPTAIN_TAKEOVER.HUMAN_ACTIVE')
      }}
    </span>
    <Button
      v-if="isCaptainActive"
      :label="t('CONVERSATION.CAPTAIN_TAKEOVER.TAKE_OVER')"
      size="xs"
      color="slate"
      no-animation
      :is-loading="isLoading"
      @click="takeOver"
    />
    <Button
      v-else
      :label="t('CONVERSATION.CAPTAIN_TAKEOVER.RESUME_CAPTAIN')"
      size="xs"
      color="slate"
      no-animation
      :is-loading="isLoading"
      @click="activateCaptain"
    />
  </div>
</template>
